/**
 * @fileoverview Vuex actions for WhatsApp Business API integration.
 *
 * @module store/modules/whatsapp/actions
 *
 * Handles WhatsApp operations including:
 * - Number registration and verification
 * - Message template management (CRUD operations)
 * - Application and profile settings
 * - Bulk messaging and scheduling
 * - Group management
 * - Message logs and analytics
 * - WhatsApp Flow builder integration
 * - Media upload and management
 */

import { rest } from "@/store/api";
import { RootState } from "@/store/types";
import { ActionTree } from "vuex/types";
import { WhatsAppState } from "./types";
import moment from "moment";
import { exportToXLSX } from "@/helpers/xlsxHelper";
import exportFilename from "@/plugins/exportFilename";
import generatePassword from "@/plugins/generatePassword";

const parseRetryErrors = (retryErrors: any): any[] => {
  if (!retryErrors || retryErrors === "undefined" || retryErrors === "null") {
    return [];
  }
  if (Array.isArray(retryErrors)) {
    return retryErrors;
  }
  if (typeof retryErrors !== "string") {
    return [retryErrors];
  }

  const trimmedRetryErrors = retryErrors.trim();
  if (!trimmedRetryErrors || trimmedRetryErrors === "undefined" || trimmedRetryErrors === "null") {
    return [];
  }

  try {
    const parsedRetryErrors = JSON.parse(trimmedRetryErrors);
    return Array.isArray(parsedRetryErrors) ? parsedRetryErrors : [parsedRetryErrors];
  } catch (err) {
    return [trimmedRetryErrors];
  }
};

export const getWhatsAppRetryCount = (message: any) => {
  if (
    message?.retry_count === undefined ||
    message.retry_count === null ||
    message.retry_count === ""
  ) {
    return 0;
  }
  return message.retry_count;
};

export const formatWhatsAppRetryMessages = (message: any) => {
  return parseRetryErrors(message?.retry_errors)
    .map((retryError) => String(retryError))
    .filter(Boolean)
    .join("\n");
};

const actions: ActionTree<WhatsAppState, RootState> = {
  /**
   * Registers a new WhatsApp Business number.
   *
   * @param payload - Registration data (phone number, verification method)
   * @returns Promise resolving to registration result
   *
   * @example
   * await dispatch('REGISTER_WHATSAPP_NUMBER', {
   *   data: { phoneNumber: '+1234567890', method: 'sms' }
   * });
   */
  REGISTER_WHATSAPP_NUMBER: (store, payload) => {
    return rest("post", "whatsapp_register", payload.data).catch((err) => {
      throw err;
    });
  },
  /**
   * Verifies a WhatsApp Business number with OTP code.
   *
   * @param payload - Verification data (code, phone number)
   * @returns Promise resolving to verification result
   *
   * @example
   * await dispatch('VERIFY_WHATSAPP_NUMBER', {
   *   data: { code: '123456', phoneNumber: '+1234567890' }
   * });
   */
  VERIFY_WHATSAPP_NUMBER: (store, payload) => {
    return rest("post", "whatsapp_verify", payload.data).catch((err) => {
      throw err;
    });
  },
  ADD_WHATSAPP_MESSAGE_TEMPLATES: ({ dispatch }, payload) => {
    return rest("post", "whatsapp_add_message_templates", payload.data)
      .then((data) => {
        dispatch("GET_WHATSAPP_MESSAGE_TEMPLATES", {
          data: { wa_endpoint: payload.data.wa_endpoint },
        });
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  UPDATE_WHATSAPP_MESSAGE_TEMPLATES: ({ dispatch }, payload) => {
    return rest("post", "whatsapp_edit_message_templates", payload.data)
      .then((data) => {
        dispatch("GET_WHATSAPP_MESSAGE_TEMPLATES", {
          data: { wa_endpoint: payload.data.wa_endpoint },
        });
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  DELETE_WHATSAPP_MESSAGE_TEMPLATES: ({ dispatch }, payload) => {
    return rest("post", "whatsapp_delete_message_templates", payload.data)
      .then((data) => {
        dispatch("GET_WHATSAPP_MESSAGE_TEMPLATES", {
          data: { wa_endpoint: payload.data.wa_endpoint },
        });
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },

  /**
   * Retrieves WhatsApp message templates for a business account.
   *
   * @param payload - Query parameters
   * @param payload.data.wa_endpoint - WhatsApp endpoint ID
   * @param payload.refresh - If true, refreshes server cache
   * @returns Promise resolving to templates array
   *
   * @example
   * const templates = await dispatch('GET_WHATSAPP_MESSAGE_TEMPLATES', {
   *   data: { wa_endpoint: 'wa-123' },
   *   refresh: false
   * });
   */
  GET_WHATSAPP_MESSAGE_TEMPLATES: (store, payload) => {
    const refreshServerCache = payload?.refresh;
    if (refreshServerCache) {
      return rest("post", "refresh_message_templates", payload.data)
        .then((data) => {
          store.commit("SET_WHATSAPP_MESSAGE_TEMPLATES", data);
          return data;
        })
        .catch((err) => {
          throw err;
        });
    }
    return rest("post", "whatsapp_get_message_templates", payload.data)
      .then((data) => {
        store.commit("SET_WHATSAPP_MESSAGE_TEMPLATES", data);
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  GET_WHATSAPP_APPLICATION_SETTINGS: (store, payload) => {
    return rest("post", "whatsapp_get_application_settings", payload.data)
      .then((data) => {
        return store.commit("SET_WHATSAPP_APPLICATION_SETTINGS", data);
      })
      .catch((err) => {
        throw err;
      });
  },
  UPDATE_WHATSAPP_APPLICATION_SETTINGS: (store, payload) => {
    return rest("post", "whatsapp_update_application_settings", payload.data)
      .then((data) => {
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  GET_WHATSAPP_PROFILE_SETTINGS: (store, payload) => {
    return rest("post", "whatsapp_get_profile_settings", payload.data)
      .then((data) => {
        return store.commit("SET_WHATSAPP_PROFILE_SETTINGS", data);
      })
      .catch((err) => {
        throw err;
      });
  },
  UPDATE_WHATSAPP_PROFILE_SETTINGS: (store, payload) => {
    return rest("post", "whatsapp_update_profile_settings", payload.data)
      .then((data) => {
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  /**
   * Sends bulk WhatsApp messages immediately.
   *
   * @param payload - Bulk send data (recipients, template, parameters)
   * @returns Promise resolving to send result
   *
   * @example
   * await dispatch('WHATSAPP_SEND_BULK', {
   *   data: {
   *     recipients: ['+1234567890', '+0987654321'],
   *     template_id: 'template-123',
   *     parameters: { name: 'John' }
   *   }
   * });
   */
  WHATSAPP_SEND_BULK: (store, payload) => {
    return rest("post", "whatsapp_send_bulk", payload.data).catch((err) => {
      throw err;
    });
  },
  /**
   * Schedules bulk WhatsApp messages for future sending.
   *
   * @param payload - Scheduling data (recipients, template, send time)
   * @returns Promise resolving to scheduling result
   *
   * @example
   * await dispatch('WHATSAPP_SCHEDULE_BULK', {
   *   data: {
   *     recipients: [...],
   *     template_id: 'template-123',
   *     scheduled_time: '2024-03-15T10:00:00'
   *   }
   * });
   */
  WHATSAPP_SCHEDULE_BULK: (store, payload) => {
    return rest("post", "whatsapp_schedule_bulk", payload.data).catch((err) => {
      throw err;
    });
  },
  WHATSAPP_CANCEL_SCHEDULED_BULK: (store, payload) => {
    return rest("post", "whatsapp_cancel_scheduled_bulk", payload.data).catch((err) => {
      throw err;
    });
  },
  WHATSAPP_REJECT_SCHEDULED_BULK: (store, payload) => {
    return rest("post", "whatsapp_reject_scheduled_bulk", payload.data).catch((err) => {
      throw err;
    });
  },
  CHECK_WHATSAPP_SERVER_STATUS: (store, payload) => {
    return rest("post", "whatsapp_get_server_status", payload.data)
      .then((data) => {
        store.commit("SET_WHATSAPP_SERVER_STATUS", data);
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  /**
   * Creates a new WhatsApp group.
   *
   * @param payload - Group creation data (name, participants)
   * @returns Promise resolving when group is created and list refreshed
   *
   * @example
   * await dispatch('WHATSAPP_CREATE_GROUP', {
   *   data: {
   *     wa_endpoint: 'wa-123',
   *     name: 'Customer Support Team',
   *     participants: ['+1234567890']
   *   }
   * });
   */
  WHATSAPP_CREATE_GROUP: (store, payload) => {
    return rest("post", "whatsapp_create_group", payload.data)
      .then((data) => {
        store.dispatch("WHATSAPP_GET_GROUPS", {
          data: { wa_endpoints: [payload.data.wa_endpoint] },
        });
        return;
      })
      .catch((err) => {
        throw err;
      });
  },
  WHATSAPP_GET_GROUPS: (store, { data, commit }) => {
    let payloadData = data;
    if (!payloadData) {
      payloadData = {};
    }
    return rest("post", "whatsapp_get_groups", payloadData)
      .then((data) => {
        if (commit) {
          store.commit("SET_WHATSAPP_GROUPS", data);
        }
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  WHATSAPP_GENERATE_INVITE_LINK: (store, payload) => {
    return rest("post", "whatsapp_generate_invite_link", payload.data).catch((err) => {
      throw err;
    });
  },
  WHATSAPP_DELETE_INVITE_LINK: (store, payload) => {
    return rest("post", "whatsapp_delete_invite_link", payload.data).catch((err) => {
      throw err;
    });
  },
  WHATSAPP_LEAVE_GROUP: (store, payload) => {
    return rest("post", "whatsapp_leave_group", payload.data)
      .then((data) => {
        store.dispatch("WHATSAPP_GET_GROUPS", {
          data: { wa_endpoints: [payload.data.wa_endpoint] },
        });
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  WHATSAPP_DELETE_GROUP_PARTICIPANTS: (store, payload) => {
    return rest("post", "whatsapp_delete_group_participants", payload.data)
      .then((data) => {
        store.dispatch("WHATSAPP_GET_GROUPS", {
          data: { wa_endpoints: [payload.data.wa_endpoint] },
        });
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  WHATSAPP_ADD_GROUP_ADMINS: (store, payload) => {
    return rest("post", "whatsapp_add_group_admins", payload.data)
      .then((data) => {
        store.dispatch("WHATSAPP_GET_GROUPS", {
          data: { wa_endpoints: [payload.data.wa_endpoint] },
        });
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  WHATSAPP_REMOVE_GROUP_ADMINS: (store, payload) => {
    return rest("post", "whatsapp_delete_group_admins", payload.data)
      .then((data) => {
        store.dispatch("WHATSAPP_GET_GROUPS", {
          data: { wa_endpoints: [payload.data.wa_endpoint] },
        });
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  WHATSAPP_SEND_MESSAGE: (store, payload) => {
    return rest("post", "whatsapp_send_message", payload.data).catch((err) => {
      throw err;
    });
  },
  WHATSAPP_UPDATE_GROUP_ICON: (store, payload) => {
    return rest("post", "whatsapp_update_group_icon", payload.data)
      .then(() => {
        store.dispatch("WHATSAPP_GET_GROUPS", {
          data: { wa_endpoints: [payload.data.wa_endpoint] },
        });
        return;
      })
      .catch((err) => {
        throw err;
      });
  },
  GET_WHATSAPP_SERVERS: (store, payload) => {
    return rest("get", "whatsapp_get_servers", {})
      .then((data) => {
        store.commit("SET_WHATSAPP_SERVERS", data);
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  GET_ACCOUNT_ANALYTICS: (store, payload) => {
    return rest("post", "whatsapp_get_account_analytics", payload.data).catch((err) => {
      throw err;
    });
  },
  GET_MESSAGE_TEMPLATE_ANALYTICS: (store, payload) => {
    return rest("post", "whatsapp_get_message_template_analytics", payload.data).catch((err) => {
      throw err;
    });
  },
  GET_MESSAGE_LOGS: (store, payload) => {
    return rest("post", "whatsapp_get_message_logs", payload.data).catch((err) => {
      throw err;
    });
  },
  GET_MESSAGE_LOGS_WITH_STATUS: (store, payload) => {
    return rest("post", "whatsapp_get_message_logs_with_status", payload.data).catch((err) => {
      throw err;
    });
  },
  GET_MESSAGE_LOGS_COUNT: (store, payload) => {
    return rest("post", "whatsapp_get_message_logs_count", payload.data).catch((err) => {
      throw err;
    });
  },
  /**
   * Downloads WhatsApp message logs with status to Excel file.
   *
   * Handles pagination, progress tracking, and password-protected Excel export.
   *
   * @param payload - Download parameters
   * @param payload.data - Filter criteria (date range, status, etc.)
   * @param payload.filenamePrefix - Optional prefix for exported filename
   * @returns Promise resolving to download result with filename and password
   *
   * @example
   * const result = await dispatch('WHATSAPP_DOWNLOAD_MESSAGE_LOGS_WITH_STATUS', {
   *   data: {
   *     from_date: '2024-01-01',
   *     to_date: '2024-01-31',
   *     status: [0, 1, 2]
   *   },
   *   filenamePrefix: 'january_messages'
   * });
   */
  WHATSAPP_DOWNLOAD_MESSAGE_LOGS_WITH_STATUS: async ({ commit, rootState }, payload: { data: any; filenamePrefix?: string }) => {
    commit("RESET_WA_DOWNLOAD");

    try {
      // 1) Get total count
      const countRes = await rest("post", "whatsapp_get_message_logs_count", payload.data).catch((err) => {
        throw err;
      });
      const status_count = (countRes && countRes.status_count) || [];
      const total = status_count.reduce((i: number, e: any) => i + e.status_count, 0);
      if (!total) {
        // Nothing to download
        return { started: false, total: 0 };
      }

      commit("SET_WA_DOWNLOAD_START", { total });

      // 2) Generate password and store it
      const password = await generatePassword();
      commit("SET_WA_DOWNLOAD_PASSWORD", { password: password || "" });

      const BATCH_SIZE = 1000;
      const exportRows: any[] = [];
      let offset = 0;
      const MESSAGE_STATUS_MAP = ["Sent", "Delivered", "Read", "Failed", "Deleted"];

      //always use SG timezone
      const tz = ((rootState as any).modules?.whatsapp?.timezone ?? 8) as number;
      const formatDateTime = (date: any) => moment(date).utcOffset(tz).format("DD-MM-YYYY HH:mm:ss");
      const getMessageParams = (message: any) => {
        if (!message.template_params || message.template_params === "undefined") {
          return "No params";
        }
        return message.template_params;
      };

      while (offset < total) {
        const res = await rest("post", "whatsapp_get_message_logs_with_status", {
          ...payload.data,
          limit: BATCH_SIZE,
          offset,
        }).catch((err) => {
          throw err;
        });

        const messages = (res && res.messages) || [];
        if (!messages.length) break;

        for (const message of messages) {
          exportRows.push({
            Time: formatDateTime(message.timestamp),
            "To number": message.receiver_ws_number,
            "Message status": MESSAGE_STATUS_MAP[message.status],
            "Template ID": message.template_id,
            Preview: message.previewMessage,
            "Template Params": getMessageParams(message),
            Note: message.note,
            "Error Log": message.status === 3 ? message.error_log : "",
            "Retry Count": getWhatsAppRetryCount(message),
            "Retry Message": formatWhatsAppRetryMessages(message),
          });
        }

        offset += messages.length;
        const processed = Math.min(offset, total);
        const progress = Math.round((processed / total) * 100);
        commit("SET_WA_DOWNLOAD_PROGRESS", { processed, progress });
      }

      const filename = exportFilename(payload.filenamePrefix || "whatsapp_message_logs_with_status", "xlsx");
      exportToXLSX(exportRows, filename, password || "");
      commit("SET_WA_DOWNLOAD_FINISH");

      return { started: true, total, filename };
    } catch (error) {
      commit("SET_WA_DOWNLOAD_FINISH");
      throw error;
    } finally {
      commit("SET_WA_DOWNLOAD_FINISH");
    }
  },
  WHATSAPP_GET_SCHEDULE_BULK: (store, payload) => {
    return rest("post", "whatsapp_get_schedule_bulk", payload.data).catch((err) => {
      throw err;
    });
  },
  WHATSAPP_GET_SENT_SCHEDULE_BULK: (store, payload) => {
    return rest("post", "whatsapp_get_sent_schedule_bulk", payload.data).catch((err) => {
      throw err;
    });
  },
  WHATSAPP_GET_SINGLE_SCHEDULE_BULK: (store, payload) => {
    return rest("post", "whatsapp_get_single_schedule_bulk", payload.data).catch((err) => {
      throw err;
    });
  },
  WHATSAPP_DELETE_PROFILE_PHOTO: (store, payload) => {
    return rest("post", "whatsapp_delete_profile_photo", payload.data).catch((err) => {
      throw err;
    });
  },
  WHATSAPP_GET_IGNORED_NUMBERS: (store, payload) => {
    return rest("post", "whatsapp_get_ignored_numbers", payload.data).catch((err) => {
      throw err;
    });
  },
  WHATSAPP_GET_MEDIA_BY_ID: (store, payload) => {
    return rest("post", "whatsapp_get_media_by_id", payload.data).catch((err) => {
      throw err;
    });
  },
  WHATSAPP_UPLOAD_PROFILE_PHOTO_CLOUD: (store, payload) => {
    const formData = new FormData();
    formData.append("file", payload);
    return rest("post", "whatsapp_cloud_api_business_profile_picture_handle", formData).catch(
      (err) => {
        throw err;
      }
    );
  },
  WHATSAPP_CLOUD_UPLOAD_MEDIA: (store, payload) => {
    const formData = new FormData();
    formData.append("file", payload.file);
    formData.append("wa_id", payload.wa_id); // Add wa_id to the form data

    return rest("post", `whatsapp_cloud_api_media_upload`, formData).catch((err) => {
      throw err;
    });
  },
  WHATSAPP_CLOUD_GET_MEDIA_BY_ID: (store, payload) => {
    return rest("post", "whatsapp_cloud_api_get_media_by_id", payload.data).catch((err) => {
      throw err;
    });
  },
  WHATSAPP_ADD_NOTE: (store, payload) => {
    return rest("post", "whatsapp_add_note", payload).catch((err) => {
      throw err;
    });
  },
  /**
   * Retrieves WhatsApp Flows (interactive forms).
   *
   * @param payload - Query parameters
   * @param payload.data.wa_endpoint - WhatsApp endpoint ID
   * @param payload.refresh - If true, refreshes from WhatsApp API
   * @returns Promise resolving to flows list
   *
   * @example
   * const flows = await dispatch('GET_FLOWS', {
   *   data: { wa_endpoint: 'wa-123' },
   *   refresh: true
   * });
   */
  GET_FLOWS: (store, payload) => {
    const refreshServerCache = payload?.refresh;
    if (refreshServerCache) {
      return rest("post", "whatsapp_refresh_flows", payload.data)
        .then((data) => {
          store.commit("SET_WA_FLOWS", data);
          return data;
        })
        .catch((err) => {
          throw err;
        });
    }
    return rest("post", "whatsapp_get_flows", payload.data)
      .then((data) => {
        store.commit("SET_WA_FLOWS", data);
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  GET_FLOW_JSON: (store, payload) => {
    return rest("post", "whatsapp_get_flow_json", payload.data)
      .then((data) => {
        store.commit("SET_SELECTED_FLOW", data);
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  /**
   * Creates or updates a WhatsApp Flow.
   *
   * @param payload - Flow configuration (JSON schema, metadata)
   * @returns Promise resolving to created/updated flow
   *
   * @example
   * await dispatch('SUBMIT_FLOW', {
   *   name: 'Contact Form',
   *   json_schema: { ... },
   *   wa_endpoint: 'wa-123'
   * });
   */
  SUBMIT_FLOW: (store, payload) => {
    return rest("post", "whatsapp_create_flow", payload);
  },
  DELETE_WHATSAPP_FLOW: (store, payload) => {
    return rest("post", "whatsapp_delete_flow", payload);
  },
  DEPRECATE_WHATSAPP_FLOW: (store, payload) => {
    return rest("post", "whatsapp_deprecate_flow", payload);
  },
  PUBLISH_WHATSAPP_FLOW: (store, payload) => {
    return rest("post", "whatsapp_publish_flow", payload);
  },

  PREVIEW_TEMPLATE: (store, payload) => {
    return rest("post", "whatsapp_preview_template", payload);
  },
};

export default actions;
